import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiResponse, ApiService } from '../api.service';
import { requireSuccess, unwrapApiEnvelope } from '../../shared/data/api-envelope';
import { GroupId, UserId, asId, idToNumber } from '../../shared/interfaces/ids';
import {
  AddUsersResult,
  AddUsersToGroupResponse,
  Group,
  GroupDetail,
  GroupDetailResponse,
  GroupMember,
  GroupMemberResponse,
  GroupResponse,
  NewGroup,
  PrimaryGroup,
  PrimaryGroupResponse,
  RemoveUsersFromGroupResponse,
  RemoveUsersResult,
  UserGroupMembership,
  UserGroupMembershipResponse,
} from '../../interfaces/group';

/**
 * The group-manager endpoints of `routes/group_routes.py`.
 *
 * Every method here was rewritten against the handler and its model method — see
 * .notes/API_DATA_TYPE_AUDIT_FINDINGS.md §1. Three things changed beyond the types:
 *
 * 1. **The batch membership endpoints return their real result.** `POST` and `DELETE`
 *    on `/v1/groups/<id>/users` report per-user outcomes and answer 200 with
 *    `success: true` even when only some users were changed. The old code mapped all of
 *    it to `true`, so a dialog adding five users reported five added when two IDs were
 *    invalid.
 * 2. **`response.success || true` is gone.** It can never be `false`, so it made every
 *    mutation's return value a constant while looking like a check.
 * 3. **Three methods were deleted, not fixed** — `searchUsers` and `searchGroups` called
 *    `/v1/users/search` and `/v1/groups/search`, which do not exist on the backend
 *    (Flask 404, HTML body), and `updateGroup` used `PUT` where the route registers
 *    `PATCH` (405). None had a caller outside its own spec. Reintroduce them against a
 *    real route if the feature is wanted.
 *
 * `getAllUsers()` is gone too: it was a second, wrongly-typed implementation of
 * `GET /v1/users`, which `user-management/user.service.ts` already owns and maps
 * correctly. The group manager now calls that one.
 *
 * IDs are branded strings from here outwards. `POST /v1/groups/<id>/users` is the one
 * endpoint in this family that genuinely requires integers in the body — it type-checks
 * `users` with `isinstance(x, (int, list))` and 400s a string — so it converts back with
 * `idToNumber`.
 */
@Injectable({
  providedIn: 'root',
})
export class GroupService {
  private apiService = inject(ApiService);

  // --- Groups ---

  getGroups(): Observable<Group[]> {
    return this.apiService.get<ApiResponse<GroupResponse[]>>(`/v1/groups`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`Couldn’t load groups.` }),
      map((rows) => rows.map(toGroup))
    );
  }

  getGroupById(groupId: GroupId): Observable<GroupDetail> {
    return this.apiService
      .get<ApiResponse<GroupDetailResponse>>(`/v1/groups/${groupId}`)
      .pipe(unwrapApiEnvelope({ fallbackMessage: $localize`Couldn’t load the group.` }), map(toGroupDetail));
  }

  /**
   * The handler reads `Name` and `Description` and nothing else — `OrgID` comes from the
   * token — so the parameter is {@link NewGroup} rather than a partial `Group`, and the
   * PascalCase body is built here rather than being the caller's shape.
   * Answers 201, and 409 with "Group Name already exists" on a duplicate.
   */
  createGroup(group: NewGroup): Observable<GroupDetail> {
    return this.apiService
      .post<ApiResponse<GroupDetailResponse>>(`/v1/groups`, { Name: group.name, Description: group.description })
      .pipe(unwrapApiEnvelope({ fallbackMessage: $localize`Couldn’t create the group.` }), map(toGroupDetail));
  }

  /**
   * Answers 200 with a literal `success: true`, and 404 / 400 / 500 for every failure it
   * currently has — so the error arm already fires and there is nothing in the body
   * worth reading.
   *
   * It still goes through `requireSuccess`. The old `response.success || true` could
   * never be `false`, which meant a `success: false` on a 200 would have been reported
   * as a deleted group; that the route does not send one today is a fact about this
   * handler, not a property of the contract, and it is one edit away from changing.
   */
  deleteGroup(groupId: GroupId): Observable<void> {
    return this.apiService
      .delete<ApiResponse<never>>(`/v1/groups/${groupId}`)
      .pipe(requireSuccess($localize`Couldn’t delete the group.`));
  }

  // --- Membership ---

  getUsersForGroup(groupId: GroupId): Observable<GroupMember[]> {
    return this.apiService.get<ApiResponse<GroupMemberResponse[]>>(`/v1/groups/${groupId}/users`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`Couldn’t load the group’s users.` }),
      map((rows) => rows.map(toGroupMember))
    );
  }

  getGroupsForUser(userId: UserId): Observable<UserGroupMembership[]> {
    return this.apiService.get<ApiResponse<UserGroupMembershipResponse[]>>(`/v1/users/${userId}/groups`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`Couldn’t load the user’s groups.` }),
      map((rows) => rows.map(toUserGroupMembership))
    );
  }

  /**
   * Adds users to a group, reporting what actually happened to each.
   *
   * A batch: `added`, `already_member` and `invalid_ids` come back together, and a mixed
   * outcome is a 200 with `success: true` and the message "Partial success". Callers
   * must not report the number they asked for as the number that landed.
   */
  addUsersToGroup(userIds: UserId[], groupId: GroupId): Observable<AddUsersResult> {
    return this.apiService
      .post<ApiResponse<AddUsersToGroupResponse>>(`/v1/groups/${groupId}/users`, {
        // The handler requires integers here and 400s anything else.
        users: userIds.map(idToNumber),
      })
      .pipe(
        unwrapApiEnvelope({ fallbackMessage: $localize`Couldn’t add users to the group.` }),
        map((result) => ({
          added: result.added.map((entry) => ({ userId: asId<'User'>(entry.UserID), isPrimary: entry.IsPrimary })),
          alreadyMember: result.already_member.map((entry) => ({
            userId: asId<'User'>(entry.UserID),
            isPrimary: entry.IsPrimary,
          })),
          invalidIds: result.invalid_ids.map((id) => asId<'User'>(id)),
        }))
      );
  }

  /**
   * Removes users from a group.
   *
   * `blockedAsPrimary` (the wire's `protected`) is the primary-group guard: a user
   * cannot be removed from their primary group, and a batch where that happens to some
   * users still answers 200 with `success: true`.
   */
  removeUsersFromGroup(userIds: UserId[], groupId: GroupId): Observable<RemoveUsersResult> {
    return this.apiService
      .delete<ApiResponse<RemoveUsersFromGroupResponse>>(`/v1/groups/${groupId}/users`, {
        users: userIds.map(idToNumber),
      })
      .pipe(
        unwrapApiEnvelope({ fallbackMessage: $localize`Couldn’t remove users from the group.` }),
        map((result) => ({
          removed: result.removed.map((id) => asId<'User'>(id)),
          blockedAsPrimary: result.protected.map((id) => asId<'User'>(id)),
          notMember: result.not_member.map((id) => asId<'User'>(id)),
          invalidIds: result.invalid_ids.map((id) => asId<'User'>(id)),
        }))
      );
  }

  /**
   * Sets a user's primary group and returns the group that is now primary.
   *
   * `data` can be `null`: the handler sends whatever `get_primary_group_info` returns,
   * unguarded, alongside `success: true` (F5). `unwrapApiEnvelope` turns that into a
   * thrown error rather than a null the caller has to remember to check.
   */
  setPrimaryGroup(userId: UserId, groupId: GroupId): Observable<PrimaryGroup> {
    return this.apiService
      .patch<ApiResponse<PrimaryGroupResponse>>(`/v1/users/${userId}/primary-group`, { GroupID: idToNumber(groupId) })
      .pipe(
        unwrapApiEnvelope({ fallbackMessage: $localize`Couldn’t update the primary group.` }),
        map((row) => ({
          groupId: asId<'Group'>(row.GroupID),
          name: row.Name,
          description: row.Description,
          isPrimary: row.IsPrimary,
        }))
      );
  }
}

function toGroup(row: GroupResponse): Group {
  return {
    groupId: asId<'Group'>(row.GroupID),
    name: row.Name,
    description: row.Description,
    isDefault: row.IsDefault,
    isArchived: row.IsArchived,
  };
}

function toGroupDetail(row: GroupDetailResponse): GroupDetail {
  return {
    groupId: asId<'Group'>(row.GroupID),
    name: row.Name,
    description: row.Description,
    isDefault: row.IsDefault,
  };
}

function toGroupMember(row: GroupMemberResponse): GroupMember {
  return {
    userGroupId: asId<'Membership'>(row.UserGroupID),
    userId: asId<'User'>(row.UserID),
    groupId: asId<'Group'>(row.GroupID),
    isPrimary: row.IsPrimary,
    userName: row.UserName,
    email: row.Email,
  };
}

function toUserGroupMembership(row: UserGroupMembershipResponse): UserGroupMembership {
  return {
    userGroupId: asId<'Membership'>(row.UserGroupID),
    userId: asId<'User'>(row.UserID),
    groupId: asId<'Group'>(row.GroupID),
    isPrimary: row.IsPrimary,
    name: row.Name,
    description: row.Description,
  };
}
